import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Edit, Calendar, Bed } from 'lucide-react';
import LoginSignupModal from '../components/modals/LoginSignupModal';
import DatePickerModal from '../components/modals/DatePickerModal';
import GuestSelectorModal from '../components/modals/GuestSelectorModal';
import { staysApi } from '../services/staysApi';
import { useAuth } from '../hooks/useAuth';
import { useDateContext } from '../contexts/DateContext';
import { useGuestContext } from '../contexts/GuestContext';
import { getVisibleDotIndices } from '../utils/carouselDots';
import { mapStayRoomForSelectionUI } from '../utils/roomDayPricing';
import {
  adultsForRoomDisplayQuote,
  canAddRoomUnit,
  getRoomCapacityBlockMessage,
  getRoomNumericCapacity,
  isRoomSelectionValidForGuests,
  sortRoomsForGuestFilters
} from '../utils/roomGuestPricing';
import { buildStayPricingPayload } from '../utils/bookingPricing';
import { isStayComingSoon } from '../utils/stayComingSoon';
import { checkRoomAvailability } from '../services/inventoryApi';
import { dateToYmd } from '../utils/roomDayPricing';
import { releaseStalePendingStayBooking } from '../utils/pendingStayBooking';
import { useDebouncedEffect } from '../hooks/useDebouncedEffect';
import { ROOM_BOOKING_OPTIONS } from '../utils/roomBookingOptions';
import RoomBookingOptionPicker from '../components/RoomBookingOptionPicker';

const RoomSelection = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Auth context
  const { user, isAuthenticated } = useAuth();
  
  // Get property ID from URL or location state
  const propertyId = id || location.state?.property?.id;
  
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [property, setProperty] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [roomImageStates, setRoomImageStates] = useState({});
  const [touchStart, setTouchStart] = useState({});
  const [touchEnd, setTouchEnd] = useState({});
  
  // New state for dynamic room data
  const [roomTypes, setRoomTypes] = useState([]);
  const [catalogRooms, setCatalogRooms] = useState([]);
  const [catalogStay, setCatalogStay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // New state for room selection and quantities
  const [roomQuantities, setRoomQuantities] = useState({});
  const [selectedRooms, setSelectedRooms] = useState([]);
  
  // Guest selection state - use global context
  const { guestData: globalGuestData, updateGuests } = useGuestContext();
  const [guestData, setGuestData] = useState(globalGuestData);
  const [showGuestSelector, setShowGuestSelector] = useState(false);

  // Sync with global guests when they change
  useEffect(() => {
    setGuestData(globalGuestData);
  }, [globalGuestData]);

  // Reconcile stale checkout once per property visit (not on every query-string tweak)
  useEffect(() => {
    releaseStalePendingStayBooking();
  }, [propertyId]);

  // Date selection state
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  const { selectedDates: globalDates, updateDates } = useDateContext();

  // Initialize selectedDates from search params if available, otherwise use global dates
  const initializeDatesFromParams = () => {
    const checkIn = searchParams.get('checkIn');
    const checkOut = searchParams.get('checkOut');
    
    if (checkIn && checkOut) {
      const checkInDate = new Date(checkIn);
      const checkOutDate = new Date(checkOut);
      
      // Format dates for display
      const formatDate = (date) => {
        const options = { weekday: 'short', day: 'numeric', month: 'short' };
        return date.toLocaleDateString('en-US', options);
      };
      
      return {
        checkIn: formatDate(checkInDate), // Formatted string for display
        checkOut: formatDate(checkOutDate), // Formatted string for display
        checkInDate: checkInDate, // Store actual Date object for DatePickerModal
        checkOutDate: checkOutDate, // Store actual Date object for DatePickerModal
        checkInTime: "2:00 PM",
        checkOutTime: "11:00 AM",
        duration: Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24))
      };
    }

    // Use global dates if no search params
    if (globalDates.checkInDate && globalDates.checkOutDate) {
      const formatDate = (date) => {
        const options = { weekday: 'short', day: 'numeric', month: 'short' };
        return date.toLocaleDateString('en-US', options);
      };

      return {
        checkIn: globalDates.checkIn,
        checkOut: globalDates.checkOut,
        checkInDate: globalDates.checkInDate,
        checkOutDate: globalDates.checkOutDate,
        checkInTime: "2:00 PM",
        checkOutTime: "11:00 AM",
        duration: Math.ceil((globalDates.checkOutDate - globalDates.checkInDate) / (1000 * 60 * 60 * 24))
      };
    }
    
    return {
      checkIn: null,
      checkOut: null,
      checkInDate: null, // Store actual Date object for DatePickerModal
      checkOutDate: null, // Store actual Date object for DatePickerModal
      checkInTime: "2:00 PM",
      checkOutTime: "11:00 AM",
      duration: null
    };
  };
  
  const [selectedDates, setSelectedDates] = useState(initializeDatesFromParams);
  const [hasSelectedDates, setHasSelectedDates] = useState(
    searchParams.get('checkIn') && searchParams.get('checkOut') || (globalDates.checkInDate && globalDates.checkOutDate)
  );

  // Sync with global dates when they change (if no search params)
  useEffect(() => {
    if (!searchParams.get('checkIn') && !searchParams.get('checkOut') && globalDates.checkInDate && globalDates.checkOutDate) {
      const formatDate = (date) => {
        const options = { weekday: 'short', day: 'numeric', month: 'short' };
        return date.toLocaleDateString('en-US', options);
      };

      setSelectedDates({
        checkIn: globalDates.checkIn,
        checkOut: globalDates.checkOut,
        checkInDate: globalDates.checkInDate,
        checkOutDate: globalDates.checkOutDate,
        checkInTime: "2:00 PM",
        checkOutTime: "11:00 AM",
        duration: Math.ceil((globalDates.checkOutDate - globalDates.checkInDate) / (1000 * 60 * 60 * 24))
      });
      setHasSelectedDates(true);
    }
  }, [globalDates, searchParams]);

  // Get property data from location state or fetch it
  useEffect(() => {
    if (location.state?.property) {
      setProperty({
        ...location.state.property,
        id: location.state.property.id || id, // Ensure property has ID
        // Preserve latitude/longitude if they exist in location.state.property
        latitude: location.state.property.latitude || null,
        longitude: location.state.property.longitude || null
      });
    } else if (id) {
      // Property data will be set when we fetch the stay information
      setProperty({
        id: id, // Use the ID from URL params
        name: "Loading...",
        city: "Loading...",
        state: "Loading...",
        latitude: null,
        longitude: null
      });
    }
  }, [id, location.state]);

  // Fetch rooms from backend
  useEffect(() => {
    const fetchRooms = async () => {
      if (!propertyId) {
        console.log('No propertyId available, skipping room fetch');
        setLoading(false);
        setError('No property ID available. Please navigate back and try again.');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching rooms for property:', propertyId);
        
        // Fetch stay details which includes rooms
        const response = await staysApi.getStayById(propertyId);
        console.log('Stay API response:', response);
        
        const stayData = response.stay;
        
        if (stayData && stayData.rooms && stayData.rooms.length > 0) {
          console.log('Found rooms:', stayData.rooms);
          
          // Update property data with actual data from API
          if (stayData) {
            const propertyData = {
              id: stayData.stayId || propertyId, // Use stayId from API
              name: stayData.title || stayData.name || "Property",
              city: stayData.city || "City not specified",
              state: stayData.state || "State not specified",
              location: stayData.address || stayData.location || "Location not specified",
              image: stayData.images?.[0] || "https://images.unsplash.com/photo-1552053831-71594a27632d?w=400&h=300&fit=crop",
              latitude: stayData.latitude || null,
              longitude: stayData.longitude || null,
              cancellationPolicy: stayData.cancellationPolicy || '',
              houseRules: stayData.houseRules || []
            };
            
            console.log('Setting property data:', propertyData);
            setProperty(propertyData);
          }
          
          setCatalogStay(stayData);
          setCatalogRooms(stayData.rooms);
          const transformedRooms = stayData.rooms.map((room) =>
            mapStayRoomForSelectionUI(
              room,
              selectedDates,
              Math.min(Math.max(1, guestData?.adults || 2), room.capacity || 10),
              stayData
            )
          );

          setRoomTypes(transformedRooms);
          setSelectedOptions((prev) => {
            const next = { ...prev };
            transformedRooms.forEach((room) => {
              if (next[room.id] == null) {
                next[room.id] = ROOM_BOOKING_OPTIONS.ROOM_ONLY;
              }
            });
            return next;
          });
          
          // Initialize room quantities and selected rooms
          setRoomQuantities({});
          setSelectedRooms([]);
        } else {
          setRoomTypes([]);
          setError('No room options available for this property.');
        }
      } catch (err) {
        console.error('Error fetching rooms:', err);
        setError('Failed to load room options. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchRooms();
  }, [propertyId, selectedDates?.checkInDate, selectedDates?.checkOutDate]);

  const catalogRoomIds = useMemo(
    () =>
      catalogRooms
        .map((room) => room.roomId || room.id)
        .filter(Boolean)
        .join(','),
    [catalogRooms]
  );

  useDebouncedEffect(
    () => {
      if (!propertyId || !hasSelectedDates || !catalogRooms.length) return undefined;

      const checkIn = dateToYmd(selectedDates.checkInDate);
      const checkOut = dateToYmd(selectedDates.checkOutDate);
      if (!checkIn || !checkOut || checkIn >= checkOut) return undefined;

      let cancelled = false;

      (async () => {
        const results = await Promise.all(
          catalogRooms.map(async (room) => {
            const roomId = room.roomId || room.id;
            if (!roomId) return { roomId, soldOut: false };
            try {
              const data = await checkRoomAvailability({
                stayId: propertyId,
                roomId,
                checkIn,
                checkOut,
                quantity: 1
              });
              return { roomId, soldOut: data.canAccommodate !== true };
            } catch {
              return { roomId, soldOut: false };
            }
          })
        );

        if (cancelled) return;

        const soldOutIds = new Set(
          results.filter((r) => r.soldOut).map((r) => r.roomId)
        );

        setRoomTypes((prev) =>
          prev.map((room) => ({
            ...room,
            inventorySoldOut: soldOutIds.has(room.id)
          }))
        );
      })();

      return () => {
        cancelled = true;
      };
    },
    [
      propertyId,
      hasSelectedDates,
      catalogRoomIds,
      selectedDates?.checkInDate,
      selectedDates?.checkOutDate
    ],
    450
  );

  // Auto-open date picker if no dates are selected
  useEffect(() => {
    if (!hasSelectedDates && !loading && roomTypes.length > 0) {
      // Small delay to ensure component is fully rendered
      const timer = setTimeout(() => {
        setShowDatePicker(true);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [hasSelectedDates, loading, roomTypes.length]);

  // Function to handle room option changes
  const handleOptionChange = (roomId, option) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [roomId]: option,
    }));

    setSelectedRooms((prev) =>
      prev.map((room) =>
        room.id === roomId ? { ...room, selectedOption: option } : room
      )
    );
  };

  const getRoomSelectedOption = (roomId) =>
    selectedOptions[roomId] || ROOM_BOOKING_OPTIONS.ROOM_ONLY;

  const isRoomBookingBlocked = (room) =>
    room.comingSoon || room.inventorySoldOut || !canAddRoomUnit(room, guestData, roomQuantities, selectedRooms);

  const handleBookRoom = (room) => {
    if (isRoomBookingBlocked(room)) {
      return;
    }
    // For single room booking
    const singleRoomSelection = {
      type: 'single',
      ...room,
      quantity: 1,
      selectedOption: getRoomSelectedOption(room.id)
    };
    
    console.log('Single room selection:', singleRoomSelection);
    console.log('handleBookRoom - isAuthenticated:', isAuthenticated);
    console.log('handleBookRoom - user:', user);
    
    setSelectedRoom(singleRoomSelection);
    
    // Check if user is already authenticated
    if (isAuthenticated && user) {
      // User is logged in, proceed directly to booking review
      console.log('User is authenticated, proceeding to booking review');
      const pricing = buildStayPricingPayload(
        [{ ...singleRoomSelection, quantity: 1 }],
        selectedDates
      );
      navigate(`/property/${propertyId}/booking-review`, {
        state: {
          bookingData: {
            property,
            selectedRoom: singleRoomSelection,
            dates: selectedDates,
            user: user,
            pricing: {
              roomPrice: pricing.roomPrice,
              taxes: pricing.taxes,
              total: pricing.total
            }
          }
        }
      });
    } else {
      // User is not logged in, show login modal
      console.log('User is not authenticated, showing login modal');
      setShowLoginModal(true);
    }
  };

  const sortedRoomTypes = useMemo(
    () => sortRoomsForGuestFilters(roomTypes, guestData, roomQuantities, selectedRooms),
    [roomTypes, guestData, roomQuantities, selectedRooms]
  );

  // Clear selection when guest filters can no longer be satisfied
  useEffect(() => {
    if (!selectedRooms.length) return;
    if (isRoomSelectionValidForGuests(guestData, roomQuantities, selectedRooms)) return;
    setRoomQuantities({});
    setSelectedRooms([]);
  }, [guestData?.rooms, guestData?.adults, guestData?.children]);

  const stayComingSoon = useMemo(
    () => isStayComingSoon(catalogStay),
    [catalogStay]
  );

  const renderSleepsLabel = (room, canAdd) => {
    if (canAdd) {
      return <span className="text-[12px] text-[#bebebe]">Sleeps {room.sleeps}</span>;
    }
    const cap = getRoomNumericCapacity(room) || room.sleeps;
    return <span className="text-[12px] text-[#b45309]">Sleeps up to {cap}</span>;
  };

  const renderRoomPriceLabel = (room, { compact = false } = {}) => {
    if (room.comingSoon) {
      return (
        <span
          className={`font-bold text-[#78736A] leading-none ${
            compact ? 'text-[14px] leading-tight' : 'text-[14px]'
          }`}
        >
          Coming soon
        </span>
      );
    }
    if (room.inventorySoldOut) {
      return (
        <span
          className={`font-bold text-[#78736A] leading-none ${
            compact ? 'text-[14px] leading-tight' : 'text-[14px]'
          }`}
        >
          Sold out
        </span>
      );
    }
    if (typeof room.price === 'number') {
      return (
        <div className={`flex items-center ${compact ? 'space-x-2' : 'gap-[10px]'}`}>
          <span
            className={`font-bold text-black ${
              compact ? 'text-[14px] leading-tight' : 'text-[14px] leading-none'
            }`}
          >
            ₹{room.price.toLocaleString()}
          </span>
        </div>
      );
    }
    return (
      <div className={compact ? 'text-[14px] text-gray-600' : 'text-[18px] text-gray-600'}>
        Price on request
      </div>
    );
  };

  const renderCapacityBlockedAction = (message, { compact = false } = {}) => (
    <span
      className={`inline-flex items-center justify-center rounded-[12px] bg-[#E5E3DF] text-center font-medium text-[#78736A] whitespace-nowrap ${
        compact
          ? 'min-h-[44px] px-[12px] text-[11px] leading-none'
          : 'h-[44px] px-[16px] text-[12px] leading-none'
      }`}
    >
      {message}
    </span>
  );

  const renderBookRoomAction = (room, canAdd) => {
    if (room.comingSoon) {
      return (
        <span className="inline-flex h-[44px] items-center rounded-[12px] bg-[#E5E3DF] px-[36px] text-[16px] font-bold text-[#78736A]">
          Coming soon
        </span>
      );
    }
    if (room.inventorySoldOut) {
      return (
        <span className="inline-flex h-[44px] items-center rounded-[12px] bg-[#E5E3DF] px-[36px] text-[16px] font-bold text-[#78736A]">
          Sold out
        </span>
      );
    }
    if (!canAdd) {
      const message =
        getRoomCapacityBlockMessage(room, guestData, roomQuantities, selectedRooms) ||
        'Not enough space for your guests';
      return renderCapacityBlockedAction(message);
    }
    return (
      <button
        type="button"
        onClick={() => handleBookRoom(room)}
        className="h-[44px] rounded-[12px] bg-black px-[36px] text-[16px] font-bold text-white transition-colors hover:bg-gray-800"
      >
        BOOK ROOM
      </button>
    );
  };

  const renderRoomAddControls = (room) => {
    if (room.comingSoon) {
      return (
        <span className="inline-flex h-[44px] items-center rounded-[12px] bg-[#E5E3DF] px-[24px] text-[14px] font-semibold text-[#78736A]">
          Coming soon
        </span>
      );
    }
    if (room.inventorySoldOut) {
      return (
        <span className="inline-flex h-[44px] items-center rounded-[12px] bg-[#E5E3DF] px-[24px] text-[14px] font-semibold text-[#78736A]">
          Sold out
        </span>
      );
    }
    const canAdd = canAddRoomUnit(room, guestData, roomQuantities, selectedRooms);
    const capacityMessage = getRoomCapacityBlockMessage(
      room,
      guestData,
      roomQuantities,
      selectedRooms
    );
    const qty = roomQuantities[room.id];

    return qty ? (
          <div className="flex items-center justify-between w-[120px] h-[44px] px-[24px] rounded-[12px] border border-[#FF5E4C]">
            <button type="button" onClick={() => handleRemoveRoom(room.id)} aria-label="Remove room">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                <g clipPath="url(#clip0_2956_479)">
                  <path d="M3.75 9H14.25" stroke="#FF5E4C" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
                </g>
                <defs>
                  <clipPath id="clip0_2956_479">
                    <rect width="18" height="18" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </button>
            <span className="text-[16px] font-semibold text-[#6b6b6b] min-w-[20px] text-center">{qty}</span>
            <button
              type="button"
              disabled={!canAdd}
              onClick={() => canAdd && handleAddRoom(room)}
              aria-label="Add another room"
              className={!canAdd ? 'opacity-40 cursor-not-allowed' : ''}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                <g clipPath="url(#clip0_2956_483)">
                  <path d="M9 3.75002V14.25" stroke="#FF5E4C" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M3.75 9H14.25" stroke="#FF5E4C" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
                </g>
                <defs>
                  <clipPath id="clip0_2956_483">
                    <rect width="18" height="18" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </button>
          </div>
        ) : !canAdd && capacityMessage ? (
          renderCapacityBlockedAction(capacityMessage, { compact: true })
        ) : (
          <button
            type="button"
            disabled={!canAdd}
            onClick={() => canAdd && handleAddRoom(room)}
            className={`h-[44px] w-[120px] rounded-[12px] text-[16px] px-[24px] font-semibold ${
              canAdd
                ? 'bg-black text-white'
                : 'bg-[#e5e5e5] text-[#999] cursor-not-allowed'
            }`}
          >
            Add
          </button>
    );
  };

  // Function to handle adding a room
  const handleAddRoom = (room) => {
    if (isRoomBookingBlocked(room)) {
      return;
    }
    console.log('RoomSelection - handleAddRoom - room:', room);
    
    setRoomQuantities(prev => ({
      ...prev,
      [room.id]: (prev[room.id] || 0) + 1
    }));
    
    // Add to selected rooms if not already there
    if (!selectedRooms.find(r => r.id === room.id)) {
      const roomToAdd = {
        ...room,
        quantity: 1,
        selectedOption: getRoomSelectedOption(room.id)
      };
      console.log('RoomSelection - handleAddRoom - adding room:', roomToAdd);
      
      setSelectedRooms(prev => {
        const newSelectedRooms = [...prev, roomToAdd];
        console.log('RoomSelection - handleAddRoom - new selectedRooms:', newSelectedRooms);
        return newSelectedRooms;
      });
    }
  };

  // Function to handle removing a room
  const handleRemoveRoom = (roomId) => {
    setRoomQuantities(prev => {
      const newQuantities = { ...prev };
      if (newQuantities[roomId] > 1) {
        newQuantities[roomId] -= 1;
      } else {
        delete newQuantities[roomId];
        // Remove from selected rooms
        setSelectedRooms(prev => prev.filter(r => r.id !== roomId));
      }
      return newQuantities;
    });
  };

  // Function to handle quantity change
  const handleQuantityChange = (roomId, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveRoom(roomId);
    } else {
      setRoomQuantities(prev => ({
        ...prev,
        [roomId]: newQuantity
      }));
      
      // Update selected rooms
      setSelectedRooms(prev => prev.map(room => 
        room.id === roomId ? { ...room, quantity: newQuantity } : room
      ));
    }
  };

  // Helper functions for pricing
  const getTotalPrice = () => {
    const rooms = selectedRooms.map((room) => ({
      ...room,
      quantity: roomQuantities[room.id] || 1
    }));
    return buildStayPricingPayload(rooms, selectedDates).total;
  };

  const getTotalRooms = () => {
    return Object.values(roomQuantities).reduce((total, quantity) => total + quantity, 0);
  };



  // Function to handle proceeding to booking
  const handleProceedToBooking = () => {
    if (selectedRooms.length === 0) {
      alert('Please select at least one room before proceeding');
      return;
    }
    
    if (!hasSelectedDates || !selectedDates) {
      alert('Please select check-in and check-out dates before proceeding');
      setShowDatePicker(true);
      return;
    }
    
    // Prepare the selected room data with dates and guest information
    const roomData = {
      type: selectedRooms.length === 1 ? 'single' : 'multiple',
      rooms: selectedRooms.map(room => ({
        ...room,
        quantity: roomQuantities[room.id] || 1,
        selectedOption: getRoomSelectedOption(room.id)
      })),
      guestData: guestData // Include guest selection data
    };
    
    console.log('RoomSelection - handleProceedToBooking - roomData:', roomData);
    console.log('RoomSelection - selectedRooms:', selectedRooms);
    console.log('RoomSelection - roomQuantities:', roomQuantities);
    console.log('RoomSelection - selectedOptions:', selectedOptions);
    console.log('RoomSelection - isAuthenticated:', isAuthenticated);
    console.log('RoomSelection - user:', user);
    
    setSelectedRoom(roomData);
    
    // Check if user is already authenticated
    if (isAuthenticated && user) {
      // User is logged in, proceed directly to booking review
      console.log('User is authenticated, proceeding to booking review');
      
      const pricing = buildStayPricingPayload(roomData.rooms, selectedDates);

      navigate(`/property/${propertyId}/booking-review`, {
        state: {
          bookingData: {
            property,
            selectedRooms: roomData.rooms,
            dates: selectedDates,
            user: user,
            guestData: guestData,
            pricing: {
              roomPrice: pricing.roomPrice,
              taxes: pricing.taxes,
              total: pricing.total
            }
          }
        }
      });
    } else {
      // User is not logged in, show login modal
      console.log('User is not authenticated, showing login modal');
      setShowLoginModal(true);
    }
  };

  // Function to handle room image change
  const handleRoomImageChange = (roomId, direction) => {
    setRoomImageStates(prev => {
      const currentState = prev[roomId] || { currentIndex: 0, maxIndex: 0 };
      const room = roomTypes.find(r => r.id === roomId);
      const maxIndex = room && room.images ? room.images.length - 1 : 0;
      
      let newIndex;
      if (direction === 'next') {
        newIndex = currentState.currentIndex >= maxIndex ? 0 : currentState.currentIndex + 1;
      } else {
        newIndex = currentState.currentIndex <= 0 ? maxIndex : currentState.currentIndex - 1;
      }
      
      return {
        ...prev,
        [roomId]: { currentIndex: newIndex, maxIndex }
      };
    });
  };

  // Date picker functions
  const handleDateSelect = (checkIn, checkOut) => {
    // Ensure we have valid date inputs
    const checkInDate = new Date(checkIn);
    const checkOutDate = new Date(checkOut);
    
    // Validate dates
    if (isNaN(checkInDate.getTime()) || isNaN(checkOutDate.getTime())) {
      console.error('Invalid date input:', { checkIn, checkOut });
      return;
    }
    
    // Calculate duration
    const diffTime = Math.abs(checkOutDate - checkInDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Format dates for display
    const formatDate = (date) => {
      const options = { weekday: 'short', day: 'numeric', month: 'short' };
      return date.toLocaleDateString('en-US', options);
    };
    
    const newDates = {
      checkIn: formatDate(checkInDate),
      checkOut: formatDate(checkOutDate),
      checkInDate: checkInDate, // Store actual Date object for DatePickerModal
      checkOutDate: checkOutDate, // Store actual Date object for DatePickerModal
      checkInTime: "2:00 PM",
      checkOutTime: "11:00 AM",
      duration: diffDays === 1 ? "1 Night" : `${diffDays} Nights`
    };

    // Update global date context
    updateDates({
      checkIn: newDates.checkIn,
      checkOut: newDates.checkOut,
      checkInDate: newDates.checkInDate,
      checkOutDate: newDates.checkOutDate
    });
    
    setSelectedDates(newDates);
    setHasSelectedDates(true);
    setShowDatePicker(false);
    
    console.log('Selected dates:', newDates);
  };

  const openDatePicker = () => {
    setShowDatePicker(true);
  };

  const closeDatePicker = () => {
    setShowDatePicker(false);
  };

  // Manual open function for when user wants to select dates after closing
  const handleManualOpenDatePicker = () => {
    setShowDatePicker(true);
  };

  // Guest selection handlers
  const handleGuestSelect = (guestString) => {
    // Parse the guest string to extract numbers (in case global context hasn't synced yet)
    const roomMatch = guestString.match(/(\d+)\s+Room/);
    const adultMatch = guestString.match(/(\d+)\s+Adult/);
    const childMatch = guestString.match(/(\d+)\s+Child/);
    const petMatch = guestString.match(/(\d+)\s+Pet/);
    
    if (roomMatch && adultMatch && childMatch && petMatch) {
      const newGuestData = {
        rooms: parseInt(roomMatch[1]),
        adults: parseInt(adultMatch[1]),
        children: parseInt(childMatch[1]),
        pets: parseInt(petMatch[1])
      };
      // Update local state (global context is already updated by GuestSelectorModal)
      setGuestData(newGuestData);
    } else {
      // Fallback: use global context
      setGuestData(globalGuestData);
    }
    setShowGuestSelector(false);
  };

  const formatGuestDisplay = () => {
    const { rooms, adults, children, pets } = guestData;
    const totalGuests = adults + children;
    return `${rooms} Room${rooms > 1 ? 's' : ''} ${totalGuests} guest${totalGuests > 1 ? 's' : ''}`;
  };

  // Sync guest data with selected rooms
  useEffect(() => {
    const totalRooms = getTotalRooms();
    if (totalRooms > 0 && totalRooms !== guestData.rooms) {
      const updatedGuestData = {
        ...guestData,
        rooms: totalRooms
      };
      setGuestData(updatedGuestData);
      updateGuests(updatedGuestData);
    }
  }, [selectedRooms, roomQuantities]);

  // Re-quote room prices when guests, dates, or selection changes
  useEffect(() => {
    if (!catalogRooms.length) return;
    const totalAdults = Math.max(1, Number(guestData?.adults) || 2);
    const bookedLines = selectedRooms.map((r) => ({
      id: r.id,
      quantity: roomQuantities[r.id] || r.quantity || 1,
      capacity: r.capacity || 10
    }));
    const totalUnits =
      bookedLines.reduce((sum, line) => sum + (line.quantity || 1), 0) || 1;

    const transformed = catalogRooms.map((room) => {
      const quoteAdults = adultsForRoomDisplayQuote(
        totalAdults,
        room.capacity,
        totalUnits,
        room.roomId,
        bookedLines.length
          ? bookedLines
          : [{ id: room.roomId, quantity: 1, capacity: room.capacity }]
      );
      return mapStayRoomForSelectionUI(room, selectedDates, quoteAdults, catalogStay);
    });
    setRoomTypes(transformed);

    setSelectedRooms((prev) =>
      prev.map((sel) => {
        const fresh = transformed.find((t) => t.id === sel.id);
        return fresh
          ? {
              ...sel,
              price: fresh.price,
              taxes: fresh.taxes,
              originalPrice: fresh.originalPrice,
              capacity: fresh.capacity,
              image: fresh.image,
              images: fresh.images
            }
          : sel;
      })
    );
  }, [catalogRooms, catalogStay, guestData?.adults, selectedDates, roomQuantities]);

  // Simple date picker modal

  // Touch event handlers for image carousel
  const onTouchStart = (e, roomId) => {
    setTouchStart(prev => ({
      ...prev,
      [roomId]: e.targetTouches[0].clientX
    }));
  };

  const onTouchMove = (e, roomId) => {
    setTouchEnd(prev => ({
      ...prev,
      [roomId]: e.targetTouches[0].clientX
    }));
  };

  const onTouchEnd = (roomId) => {
    if (!touchStart[roomId] || !touchEnd[roomId]) return;
    
    const distance = touchStart[roomId] - touchEnd[roomId];
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      handleRoomImageChange(roomId, 'next');
    } else if (isRightSwipe) {
      handleRoomImageChange(roomId, 'prev');
    }
    
    // Reset touch positions
    setTouchStart(prev => ({ ...prev, [roomId]: null }));
    setTouchEnd(prev => ({ ...prev, [roomId]: null }));
  };

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  const handleClose = () => {
    navigate(-1); // Close modal and go back
  };

  // Format dates for display (matching image format: "13 Jun -14 Jun")
  const formatDateForHeader = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short'
    });
  };

  const getHeaderDates = () => {
    // Use global dates first (most up-to-date)
    if (globalDates.checkInDate && globalDates.checkOutDate) {
      return `${formatDateForHeader(globalDates.checkInDate)} -${formatDateForHeader(globalDates.checkOutDate)}`;
    }
    // Fallback to local selectedDates
    if (selectedDates.checkInDate && selectedDates.checkOutDate) {
      return `${formatDateForHeader(selectedDates.checkInDate)} -${formatDateForHeader(selectedDates.checkOutDate)}`;
    }
    if (selectedDates.checkIn && selectedDates.checkOut) {
      // Parse formatted strings like "Wed, 13 Jun"
      const checkInMatch = selectedDates.checkIn.match(/(\d+)\s+(\w+)/);
      const checkOutMatch = selectedDates.checkOut.match(/(\d+)\s+(\w+)/);
      if (checkInMatch && checkOutMatch) {
        return `${checkInMatch[1]} ${checkInMatch[2]} -${checkOutMatch[1]} ${checkOutMatch[2]}`;
      }
      return `${selectedDates.checkIn} - ${selectedDates.checkOut}`;
    }
    return 'Select dates';
  };

  const getHeaderGuests = () => {
    // Use global guestData first (most up-to-date)
    if (globalGuestData && typeof globalGuestData.rooms === 'number' && typeof globalGuestData.adults === 'number') {
      const totalGuests = globalGuestData.adults + (globalGuestData.children || 0);
      return `${globalGuestData.rooms} Room ${totalGuests} guest`;
    }
    // Fallback to local guestData
    if (guestData && typeof guestData.rooms === 'number' && typeof guestData.adults === 'number') {
      const totalGuests = guestData.adults + (guestData.children || 0);
      return `${guestData.rooms} Room ${totalGuests} guest`;
    }
    return 'Select guests';
  };

  return (
    <>
      {/* Desktop Modal Overlay */}
      <div className="hidden md:flex fixed inset-0 bg-black bg-opacity-50 z-50 items-center justify-center p-4">
        <div className="bg-white rounded-[24px] w-full max-w-[800px] max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header - Desktop */}
          <div className="p-[16px] border-b border-[#ddd]">
            <div className="flex items-center justify-between mb-[6px]">
              <div>
                <h1 className="text-[20px] text-black leading-none tracking-[-1px] font-medium">Select Room</h1>
                {stayComingSoon && (
                  <p className="mt-2 text-[13px] text-[#78736A]">This stay is coming soon.</p>
                )}
              </div>
              <button
                onClick={handleClose}
                className="hover:bg-gray-100  transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <g clipPath="url(#clip0_3379_61)">
                    <path d="M18 6L6 18" stroke="#4B4B4B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M6 6L18 18" stroke="#4B4B4B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </g>
                  <defs>
                    <clipPath id="clip0_3379_61">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </button>
            </div>
            {/* Booking Summary - Horizontal Layout */}
            <div className="flex items-center justify-start gap-[16px]">
              <div className="flex items-center gap-[16px] text-[14px] text-[#78736A]">
                <button
                  onClick={() => setShowDatePicker(true)}
                  className="flex items-center gap-[4px] hover:text-black transition-colors cursor-pointer"
                  title="Click to edit dates"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <g clipPath="url(#clip0_calendar)">
                      <path d="M2.33301 4.08329C2.33301 3.77387 2.45592 3.47713 2.67472 3.25833C2.89351 3.03954 3.19026 2.91663 3.49967 2.91663H10.4997C10.8091 2.91663 11.1058 3.03954 11.3246 3.25833C11.5434 3.47713 11.6663 3.77387 11.6663 4.08329V11.0833C11.6663 11.3927 11.5434 11.6895 11.3246 11.9083C11.1058 12.127 10.8091 12.25 10.4997 12.25H3.49967C3.19026 12.25 2.89351 12.127 2.67472 11.9083C2.45592 11.6895 2.33301 11.3927 2.33301 11.0833V4.08329Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M9.33301 1.75V4.08333" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M4.66699 1.75V4.08333" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M2.33301 6.41663H11.6663" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_calendar">
                        <rect width="14" height="14" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <span>{getHeaderDates()}</span>
                </button>
                <button
                  onClick={() => setShowGuestSelector(true)}
                  className="flex items-center gap-[4px] hover:text-black transition-colors cursor-pointer"
                  title="Click to edit guests"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <g clipPath="url(#clip0_guests)">
                      <path d="M8.16699 7V7.00583" stroke="currentColor" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M1.75 12.25H12.25" stroke="currentColor" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M3.5 12.25V2.91667C3.5 2.60725 3.62292 2.3105 3.84171 2.09171C4.0605 1.87292 4.35725 1.75 4.66667 1.75H9.33333C9.64275 1.75 9.9395 1.87292 10.1583 2.09171C10.3771 2.3105 10.5 2.60725 10.5 2.91667V12.25" stroke="currentColor" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_guests">
                        <rect width="14" height="14" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <span>{getHeaderGuests()}</span>
                </button>
              </div>
              <button
                onClick={() => setShowDatePicker(true)}
                className="hover:bg-gray-100 rounded-full transition-colors"
                title="Edit dates and guests"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <g clipPath="url(#clip0_edit)">
                    <path d="M3.33301 16.6668H6.66634L15.4163 7.91676C15.6352 7.69789 15.8088 7.43805 15.9273 7.15208C16.0457 6.86612 16.1067 6.55962 16.1067 6.25009C16.1067 5.94056 16.0457 5.63406 15.9273 5.3481C15.8088 5.06213 15.6352 4.80229 15.4163 4.58342C15.1975 4.36455 14.9376 4.19094 14.6517 4.07248C14.3657 3.95403 14.0592 3.89307 13.7497 3.89307C13.4401 3.89307 13.1336 3.95403 12.8477 4.07248C12.5617 4.19094 12.3019 4.36455 12.083 4.58342L3.33301 13.3334V16.6668Z" stroke="#78736A" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M11.25 5.41663L14.5833 8.74996" stroke="#78736A" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                  <defs>
                    <clipPath id="clip0_edit">
                      <rect width="20" height="20" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </button>
            </div>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto bg-[#fff] scrollbar-hide">
            <div className="p-[16px] space-y-4">
              {loading && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
                  <span className="ml-3 text-gray-600">Loading room options...</span>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <p className="text-red-600 font-medium">{error}</p>
                </div>
              )}

              {!loading && roomTypes.length === 0 && !error && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                  <p className="text-gray-600 text-lg">No room options available for this property.</p>
                </div>
              )}

              {!loading && sortedRoomTypes.length > 0 && sortedRoomTypes.map((room) => {
                const canAdd =
                  !room.comingSoon &&
                  !room.inventorySoldOut &&
                  canAddRoomUnit(room, guestData, roomQuantities, selectedRooms);
                return (
                <div
                  key={room.id}
                  className={`bg-white rounded-[16px] overflow-hidden shadow-sm border border-[#FAF7F2] ${
                    !canAdd && !roomQuantities[room.id] ? 'opacity-[0.88]' : ''
                  }`}
                >
                  {/* Desktop: Horizontal Layout - Image Left, Details Right */}
                  <div className="hidden md:flex">
                    {/* Room Image - Left Side */}
                    <div className="w-[200px] flex-shrink-0 relative">
                      <div
                        className="relative w-full h-full min-h-[200px] overflow-hidden"
                        onTouchStart={(e) => onTouchStart(e, room.id)}
                        onTouchMove={(e) => onTouchMove(e, room.id)}
                        onTouchEnd={() => onTouchEnd(room.id)}
                      >
                        <div
                          className="flex transition-transform duration-300 ease-out h-full"
                          style={{
                            transform: `translateX(-${(roomImageStates[room.id]?.currentIndex || 0) * 100}%)`,
                          }}
                        >
                          {room.images.map((image, index) => (
                            <div key={index} className="w-full h-full flex-shrink-0">
                              <img
                                src={image}
                                alt={`${room.name} ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                        </div>
                        {/* Image Indicators */}
                        {room.images.length > 1 && (
                          <div className="absolute bottom-4 right-4 flex items-center space-x-[6px] z-10">
                            {getVisibleDotIndices(
                              room.images.length,
                              roomImageStates[room.id]?.currentIndex || 0
                            ).map((index) => (
                              <button
                                key={index}
                                type="button"
                                aria-label={`Image ${index + 1}`}
                                onClick={() => handleRoomImageChange(room.id, index)}
                                className={`bg-white rounded-full transition-all duration-200 ${index === (roomImageStates[room.id]?.currentIndex || 0) ? 'w-[14px] h-[14px]' : 'w-[6.5px] h-[6.5px]'}`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Room Details - Right Side */}
                    <div className="flex-1 p-[16px] flex flex-col justify-between">
                      <div>
                        {/* Room Type */}
                        <h3 className="font-medium text-[18px] text-black leading-[1.1] mb-[12px]">{room.name}</h3>

                        {/* Bed & Capacity */}
                        <div className="flex items-center gap-[16px] mb-[12px]">
                          <div className='flex items-center gap-[4px]'>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <g clipPath="url(#clip0_bed)">
                                <path d="M2.91699 5.24992C2.91699 5.55934 3.03991 5.85608 3.2587 6.07488C3.47749 6.29367 3.77424 6.41659 4.08366 6.41659C4.39308 6.41659 4.68982 6.29367 4.90862 6.07488C5.12741 5.85608 5.25033 5.55934 5.25033 5.24992C5.25033 4.9405 5.12741 4.64375 4.90862 4.42496C4.68982 4.20617 4.39308 4.08325 4.08366 4.08325C3.77424 4.08325 3.47749 4.20617 3.2587 4.42496C3.03991 4.64375 2.91699 4.9405 2.91699 5.24992Z" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M12.8337 9.91675V8.16675H1.16699" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M1.16699 4.66675V9.91675" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M7 8.16667H12.8333V7C12.8333 6.53587 12.649 6.09075 12.3208 5.76256C11.9926 5.43437 11.5475 5.25 11.0833 5.25H7V8.16667Z" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                              </g>
                              <defs>
                                <clipPath id="clip0_bed">
                                  <rect width="14" height="14" fill="white" />
                                </clipPath>
                              </defs>
                            </svg>
                            <span className='text-[12px] text-[#bebebe]'>{room.bedType}</span>
                          </div>
                          <div className='flex items-center gap-[4px]'>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <g clipPath="url(#clip0_sleeps)">
                                <path d="M2.91699 4.08333C2.91699 4.70217 3.16282 5.29566 3.60041 5.73325C4.03799 6.17083 4.63149 6.41667 5.25033 6.41667C5.86916 6.41667 6.46266 6.17083 6.90024 5.73325C7.33783 5.29566 7.58366 4.70217 7.58366 4.08333C7.58366 3.46449 7.33783 2.871 6.90024 2.43342C6.46266 1.99583 5.86916 1.75 5.25033 1.75C4.63149 1.75 4.03799 1.99583 3.60041 2.43342C3.16282 2.871 2.91699 3.46449 2.91699 4.08333Z" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M1.75 12.25V11.0833C1.75 10.4645 1.99583 9.871 2.43342 9.43342C2.871 8.99583 3.46449 8.75 4.08333 8.75H6.41667C7.0355 8.75 7.629 8.99583 8.06658 9.43342C8.50417 9.871 8.75 10.4645 8.75 11.0833V12.25" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M9.33301 1.82593C9.83492 1.95444 10.2798 2.24634 10.5975 2.65561C10.9151 3.06488 11.0876 3.56825 11.0876 4.08634C11.0876 4.60444 10.9151 5.10781 10.5975 5.51708C10.2798 5.92635 9.83492 6.21825 9.33301 6.34676" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M12.25 12.2499V11.0832C12.247 10.5682 12.0738 10.0687 11.7572 9.66247C11.4407 9.25624 10.9986 8.96614 10.5 8.8374" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                              </g>
                              <defs>
                                <clipPath id="clip0_sleeps">
                                  <rect width="14" height="14" fill="white" />
                                </clipPath>
                              </defs>
                            </svg>
                            {renderSleepsLabel(room, canAdd)}
                          </div>
                        </div>

                        {/* Booking Options */}
                        <RoomBookingOptionPicker
                          roomId={room.id}
                          roomOptions={room.options}
                          selectedOption={getRoomSelectedOption(room.id)}
                          onChange={handleOptionChange}
                          layout="inline"
                        />
                      </div>
                      {/* Divider */}
                      <div className='flex my-[12px] items-center justify-center'>
                        <svg width="100%" height="1px">
                          <line
                            x1="0" y1="1" x2="100%" y2="1"
                            stroke="#f0f0f0"
                            strokeWidth="2"
                            strokeDasharray="10 6"
                          />
                        </svg>
                      </div>

                      {/* Pricing and Book Button - Bottom */}
                      <div className="flex items-center justify-between">
                        <div className="flex gap-[14px] items-center">
                          {renderRoomPriceLabel(room)}
                          {!room.comingSoon &&
                            (room.taxes && typeof room.taxes === 'number' ? (
                              <p className="text-[12px] text-[#999] font-medium leading-none">Includes ₹{room.taxes} taxes & fees</p>
                            ) : (
                              <p className="text-[12px] text-[#999] font-medium leading-none">Taxes & fees included</p>
                            ))}
                        </div>
                        {renderBookRoomAction(room, canAdd)}
                      </div>
                    </div>
                  </div>

                  {/* Mobile: Vertical Layout (existing design) */}
                  <div className="md:hidden">
                    {/* Room Image Carousel */}
                    <div className="relative">
                      <div
                        className="relative aspect-[4/3] overflow-hidden"
                        onTouchStart={(e) => onTouchStart(e, room.id)}
                        onTouchMove={(e) => onTouchMove(e, room.id)}
                        onTouchEnd={() => onTouchEnd(room.id)}
                      >
                        <div
                          className="flex transition-transform duration-300 ease-out"
                          style={{
                            transform: `translateX(-${(roomImageStates[room.id]?.currentIndex || 0) * 100}%)`,
                          }}
                        >
                          {room.images.map((image, index) => (
                            <div key={index} className="w-full h-full flex-shrink-0">
                              <img
                                src={image}
                                alt={`${room.name} ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                        </div>
                        {/* Gradient Overlays */}
                        <div className='h-[46px] absolute bottom-0 left-0 w-full'
                          style={{
                            background: 'linear-gradient(178deg,rgb(121, 121, 121, 0) 0%,rgb(19, 19, 19, 0.33) 100%)',
                            backdropFilter: 'blur(8px)',
                            mask: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(255, 255, 255) 100%)',
                          }}
                        ></div>
                        {/* Image Indicators */}
                        <div className="absolute bottom-4 right-4 flex items-center space-x-[6px] z-10">
                          {getVisibleDotIndices(
                            room.images.length,
                            roomImageStates[room.id]?.currentIndex || 0
                          ).map((index) => (
                            <button
                              key={index}
                              type="button"
                              aria-label={`Image ${index + 1}`}
                              onClick={() => handleRoomImageChange(room.id, index)}
                              className={`bg-white rounded-full transition-all duration-200 ${index === (roomImageStates[room.id]?.currentIndex || 0) ? 'w-[14px] h-[14px]' : 'w-[6.5px] h-[6.5px]'}`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Room Details - Mobile */}
                    <div className="p-[12px] bg-white">
                      <h3 className="font-medium text-[18px] text-black leading-tight mb-[8px]">{room.name}</h3>
                      <div className="flex items-center space-x-[12px] mb-[12px]">
                        <div className='flex items-center space-x-[4px]'>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <g clipPath="url(#clip0_2846_5962)">
                              <path d="M2.91699 5.24992C2.91699 5.55934 3.03991 5.85608 3.2587 6.07488C3.47749 6.29367 3.77424 6.41659 4.08366 6.41659C4.39308 6.41659 4.68982 6.29367 4.90862 6.07488C5.12741 5.85608 5.25033 5.55934 5.25033 5.24992C5.25033 4.9405 5.12741 4.64375 4.90862 4.42496C4.68982 4.20617 4.39308 4.08325 4.08366 4.08325C3.77424 4.08325 3.47749 4.20617 3.2587 4.42496C3.03991 4.64375 2.91699 4.9405 2.91699 5.24992Z" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M12.8337 9.91675V8.16675H1.16699" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M1.16699 4.66675V9.91675" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M7 8.16667H12.8333V7C12.8333 6.53587 12.649 6.09075 12.3208 5.76256C11.9926 5.43437 11.5475 5.25 11.0833 5.25H7V8.16667Z" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                            </g>
                            <defs>
                              <clipPath id="clip0_2846_5962">
                                <rect width="14" height="14" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                          <span className='text-[12px] text-[#bebebe]'>{room.bedType}</span>
                        </div>
                        <div className='flex items-center space-x-[4px]'>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <g clipPath="url(#clip0_2846_5970)">
                              <path d="M2.91699 4.08333C2.91699 4.70217 3.16282 5.29566 3.60041 5.73325C4.03799 6.17083 4.63149 6.41667 5.25033 6.41667C5.86916 6.41667 6.46266 6.17083 6.90024 5.73325C7.33783 5.29566 7.58366 4.70217 7.58366 4.08333C7.58366 3.46449 7.33783 2.871 6.90024 2.43342C6.46266 1.99583 5.86916 1.75 5.25033 1.75C4.63149 1.75 4.03799 1.99583 3.60041 2.43342C3.16282 2.871 2.91699 3.46449 2.91699 4.08333Z" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M1.75 12.25V11.0833C1.75 10.4645 1.99583 9.871 2.43342 9.43342C2.871 8.99583 3.46449 8.75 4.08333 8.75H6.41667C7.0355 8.75 7.629 8.99583 8.06658 9.43342C8.50417 9.871 8.75 10.4645 8.75 11.0833V12.25" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M9.33301 1.82593C9.83492 1.95444 10.2798 2.24634 10.5975 2.65561C10.9151 3.06488 11.0876 3.56825 11.0876 4.08634C11.0876 4.60444 10.9151 5.10781 10.5975 5.51708C10.2798 5.92635 9.83492 6.21825 9.33301 6.34676" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M12.25 12.2499V11.0832C12.247 10.5682 12.0738 10.0687 11.7572 9.66247C11.4407 9.25624 10.9986 8.96614 10.5 8.8374" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                            </g>
                            <defs>
                              <clipPath id="clip0_2846_5970">
                                <rect width="14" height="14" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                          {renderSleepsLabel(room, canAdd)}
                        </div>
                      </div>
                      {/* Room Options - Mobile */}
                      <RoomBookingOptionPicker
                        roomId={room.id}
                        roomOptions={room.options}
                        selectedOption={getRoomSelectedOption(room.id)}
                        onChange={handleOptionChange}
                        layout="stacked"
                      />
                      <div className='mb-[12px] flex items-center justify-center'>
                        <svg width="100%" height="1px">
                          <line x1="0" y1="1" x2="100%" y2="1" stroke="#f0f0f0" strokeWidth="2" strokeDasharray="10 4" />
                        </svg>
                      </div>
                      {/* Pricing - Mobile */}
                      <div className="flex items-center justify-between">
                        <div>
                          {renderRoomPriceLabel(room, { compact: true })}
                          {!room.comingSoon &&
                            (room.taxes && typeof room.taxes === 'number' ? (
                              <p className="text-[12px] text-[#999]">Includes ₹{room.taxes} taxes & fees</p>
                            ) : (
                              <p className="text-[12px] text-[#999]">Taxes & fees included</p>
                            ))}
                        </div>
                        {renderRoomAddControls(room)}
                      </div>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Full Page */}
      <div className="md:hidden min-h-screen bg-[#FAF7F2]">
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
        {/* Header - Mobile */}
      <div className="sticky bg-[#faf7f2] top-0 z-50 p-[16px]">
        <div className="flex items-start justify-between gap-[16px]">
          <button
            onClick={handleBack}
            className=""
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <g clipPath="url(#clip0_2846_5861)">
                  <path d="M5 12H19" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M5 12L11 18" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M5 12L11 6" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_2846_5861">
                    <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </button>
          <div className='flex items-center justify-between w-full'>
            <div className=" w-full flex flex-col gap-[6px]">
              <h1 className="text-[20px] text-black leading-tight tracking-[-1px] font-medium">Select Room</h1>
              {/* Booking Summary */}
              <div className=" flex items-center justify-between text-sm text-[#78736A]">
                <div className="flex items-center space-x-[12px]">
                  <div className="flex items-center space-x-[4px]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <g clipPath="url(#clip0_2846_5871)">
                          <path d="M2.33301 4.08329C2.33301 3.77387 2.45592 3.47713 2.67472 3.25833C2.89351 3.03954 3.19026 2.91663 3.49967 2.91663H10.4997C10.8091 2.91663 11.1058 3.03954 11.3246 3.25833C11.5434 3.47713 11.6663 3.77387 11.6663 4.08329V11.0833C11.6663 11.3927 11.5434 11.6895 11.3246 11.9083C11.1058 12.127 10.8091 12.25 10.4997 12.25H3.49967C3.19026 12.25 2.89351 12.127 2.67472 11.9083C2.45592 11.6895 2.33301 11.3927 2.33301 11.0833V4.08329Z" stroke="#78736A" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M9.33301 1.75V4.08333" stroke="#78736A" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M4.66699 1.75V4.08333" stroke="#78736A" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M2.33301 6.41663H11.6663" stroke="#78736A" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M4.66699 8.75H5.83366V9.91667H4.66699V8.75Z" stroke="#78736A" strokeLinecap="round" strokeLinejoin="round" />
                      </g>
                      <defs>
                        <clipPath id="clip0_2846_5871">
                            <rect width="14" height="14" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    <button
                      onClick={handleManualOpenDatePicker}
                        className={`text-left hover:text-blue-600 transition-colors ${hasSelectedDates ? 'cursor-pointer ' : 'cursor-default'}`}
                      title={hasSelectedDates ? "Click to edit dates" : "Select dates"}
                    >
                      <span className={`text-[13px] ${!hasSelectedDates ? 'text-red-500' : ''}`}>
                        {hasSelectedDates ? `${selectedDates.checkIn} - ${selectedDates.checkOut}` : 'Select dates (Required)'}
                      </span>
                    </button>
                  </div>
                  <button 
                    onClick={() => setShowGuestSelector(true)}
                    className="flex items-center space-x-[4px] hover:text-blue-600 transition-colors cursor-pointer"
                    title="Click to edit guest count"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <g clipPath="url(#clip0_2846_5880)">
                          <path d="M8.16699 7V7.00583" stroke="currentColor" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M1.75 12.25H12.25" stroke="currentColor" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M3.5 12.25V2.91667C3.5 2.60725 3.62292 2.3105 3.84171 2.09171C4.0605 1.87292 4.35725 1.75 4.66667 1.75H9.33333C9.64275 1.75 9.9395 1.87292 10.1583 2.09171C10.3771 2.3105 10.5 2.60725 10.5 2.91667V12.25" stroke="currentColor" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                      </g>
                      <defs>
                        <clipPath id="clip0_2846_5880">
                            <rect width="14" height="14" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    <span className='text-[13px]'>{formatGuestDisplay()}</span>
                  </button>
                </div>
              </div>
            </div>
            <button 
                className={`transition-all duration-200 ${hasSelectedDates ? 'hover:bg-blue-50 hover:scale-105 cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
              onClick={() => hasSelectedDates && setShowDatePicker(true)}
              title={hasSelectedDates ? "Edit dates" : "Select dates first"}
            >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <g clipPath="url(#clip0_2846_5886)">
                    <path d="M3.33301 16.6668H6.66634L15.4163 7.91676C15.6352 7.69789 15.8088 7.43805 15.9273 7.15208C16.0457 6.86612 16.1067 6.55962 16.1067 6.25009C16.1067 5.94056 16.0457 5.63406 15.9273 5.3481C15.8088 5.06213 15.6352 4.80229 15.4163 4.58342C15.1975 4.36455 14.9376 4.19094 14.6517 4.07248C14.3657 3.95403 14.0592 3.89307 13.7497 3.89307C13.4401 3.89307 13.1336 3.95403 12.8477 4.07248C12.5617 4.19094 12.3019 4.36455 12.083 4.58342L3.33301 13.3334V16.6668Z" stroke="#307FE2" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M11.25 5.41663L14.5833 8.74996" stroke="#307FE2" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_2846_5886">
                      <rect width="20" height="20" fill="white" />
                </clipPath>
              </defs>
            </svg>
            </button>
          </div>
        </div>
      </div>

        {/* Room Options - Mobile */}
      <div className={`px-[16px] py-[20px] space-y-4 ${selectedRooms.length > 0 ? 'pb-32' : ''}`}>
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            <span className="ml-3 text-gray-600">Loading room options...</span>
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <p className="text-red-600 font-medium">{error}</p>
            <button 
              onClick={() => {
                setError(null);
                setLoading(true);
                // Retry fetching rooms
                const fetchRooms = async () => {
                  try {
                    const response = await staysApi.getStayById(propertyId);
                    const stayData = response.stay;
                    
                    if (stayData && stayData.rooms && stayData.rooms.length > 0) {
                      setCatalogStay(stayData);
                      setCatalogRooms(stayData.rooms);
                      const transformedRooms = stayData.rooms.map((room) =>
                        mapStayRoomForSelectionUI(
                          room,
                          selectedDates,
                          Math.min(Math.max(1, guestData?.adults || 2), room.capacity || 10),
                          stayData
                        )
                      );

                      setRoomTypes(transformedRooms);
                      setSelectedOptions((prev) => {
                        const next = { ...prev };
                        transformedRooms.forEach((room) => {
                          if (next[room.id] == null) {
                            next[room.id] = ROOM_BOOKING_OPTIONS.ROOM_ONLY;
                          }
                        });
                        return next;
                      });
                      
                      // Initialize room quantities and selected rooms
                      setRoomQuantities({});
                      setSelectedRooms([]);
                    } else {
                      setRoomTypes([]);
                      setError('No room options available for this property.');
                    }
                  } catch (err) {
                    setError('Retry failed. Please check your connection and try again.');
                  } finally {
                    setLoading(false);
                  }
                };
                fetchRooms();
              }} 
              className="mt-2 text-red-500 hover:text-red-700 underline"
            >
              Try again
            </button>
          </div>
        )}
        
        {!loading && roomTypes.length === 0 && !error && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <p className="text-gray-600 text-lg">No room options available for this property.</p>
            <p className="text-gray-500 text-sm mt-2">Please check back later or contact support.</p>
          </div>
        )}
        
        {!loading && sortedRoomTypes.length > 0 && sortedRoomTypes.map((room) => {
            const canAdd =
              !room.comingSoon &&
              !room.inventorySoldOut &&
              canAddRoomUnit(room, guestData, roomQuantities, selectedRooms);
            return (
            <div
              key={room.id}
              className={`bg-white rounded-[16px] overflow-hidden shadow-sm ${
                !canAdd && !roomQuantities[room.id] ? 'opacity-[0.88]' : ''
              }`}
            >
            {/* Room Image Carousel */}
            <div className="relative">
              <div 
                className="relative aspect-[4/3] overflow-hidden"
                onTouchStart={(e) => onTouchStart(e, room.id)}
                onTouchMove={(e) => onTouchMove(e, room.id)}
                onTouchEnd={() => onTouchEnd(room.id)}
              >
                <div
                  className="flex transition-transform duration-300 ease-out"
                  style={{
                    transform: `translateX(-${(roomImageStates[room.id]?.currentIndex || 0) * 100}%)`,
                  }}
                >
                  {room.images.map((image, index) => (
                    <div key={index} className="w-full h-full flex-shrink-0">
                      <img
                        src={image}
                        alt={`${room.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
                
                {/* Gradient Overlays */}
                <div className='h-[46px] absolute bottom-0 left-0 w-full'
                  style={{
                    background: 'linear-gradient(178deg,rgb(121, 121, 121, 0) 0%,rgb(19, 19, 19, 0.33) 100%)',
                    backdropFilter: 'blur(8px)',
                    mask: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(255, 255, 255) 100%)',
                  }}
                ></div>
               
 

                
 
                {/* Image Indicators */}
                <div className="absolute bottom-4 right-4 flex items-center space-x-[6px] z-10">
                  {getVisibleDotIndices(
                    room.images.length,
                    roomImageStates[room.id]?.currentIndex || 0
                  ).map((index) => (
                    <button
                      key={index}
                      type="button"
                      aria-label={`Image ${index + 1}`}
                      onClick={() => handleRoomImageChange(room.id, index)}
                        className={`bg-white rounded-full transition-all duration-200 ${index === (roomImageStates[room.id]?.currentIndex || 0) ? 'w-[14px] h-[14px]' : 'w-[6.5px] h-[6.5px] '
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Room Details */}
            <div className="p-[12px] bg-white">
              <h3 className="font-medium text-[18px] text-black leading-tight mb-[8px]">{room.name}</h3>
             
              <div className="flex items-center space-x-[12px] mb-[12px]">
                <div className='flex items-center space-x-[4px]'>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <g clip-path="url(#clip0_2846_5962)">
                        <path d="M2.91699 5.24992C2.91699 5.55934 3.03991 5.85608 3.2587 6.07488C3.47749 6.29367 3.77424 6.41659 4.08366 6.41659C4.39308 6.41659 4.68982 6.29367 4.90862 6.07488C5.12741 5.85608 5.25033 5.55934 5.25033 5.24992C5.25033 4.9405 5.12741 4.64375 4.90862 4.42496C4.68982 4.20617 4.39308 4.08325 4.08366 4.08325C3.77424 4.08325 3.47749 4.20617 3.2587 4.42496C3.03991 4.64375 2.91699 4.9405 2.91699 5.24992Z" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M12.8337 9.91675V8.16675H1.16699" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M1.16699 4.66675V9.91675" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M7 8.16667H12.8333V7C12.8333 6.53587 12.649 6.09075 12.3208 5.76256C11.9926 5.43437 11.5475 5.25 11.0833 5.25H7V8.16667Z" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_2846_5962">
                          <rect width="14" height="14" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <span className='text-[12px] text-[#bebebe]'>{room.bedType}</span>
                </div>
                <div className='flex items-center space-x-[4px]'>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <g clip-path="url(#clip0_2846_5970)">
                        <path d="M2.91699 4.08333C2.91699 4.70217 3.16282 5.29566 3.60041 5.73325C4.03799 6.17083 4.63149 6.41667 5.25033 6.41667C5.86916 6.41667 6.46266 6.17083 6.90024 5.73325C7.33783 5.29566 7.58366 4.70217 7.58366 4.08333C7.58366 3.46449 7.33783 2.871 6.90024 2.43342C6.46266 1.99583 5.86916 1.75 5.25033 1.75C4.63149 1.75 4.03799 1.99583 3.60041 2.43342C3.16282 2.871 2.91699 3.46449 2.91699 4.08333Z" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M1.75 12.25V11.0833C1.75 10.4645 1.99583 9.871 2.43342 9.43342C2.871 8.99583 3.46449 8.75 4.08333 8.75H6.41667C7.0355 8.75 7.629 8.99583 8.06658 9.43342C8.50417 9.871 8.75 10.4645 8.75 11.0833V12.25" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M9.33301 1.82593C9.83492 1.95444 10.2798 2.24634 10.5975 2.65561C10.9151 3.06488 11.0876 3.56825 11.0876 4.08634C11.0876 4.60444 10.9151 5.10781 10.5975 5.51708C10.2798 5.92635 9.83492 6.21825 9.33301 6.34676" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M12.25 12.2499V11.0832C12.247 10.5682 12.0738 10.0687 11.7572 9.66247C11.4407 9.25624 10.9986 8.96614 10.5 8.8374" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_2846_5970">
                          <rect width="14" height="14" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  {renderSleepsLabel(room, canAdd)}
                </div>
              </div>

              {/* Room Options */}
              <RoomBookingOptionPicker
                roomId={room.id}
                roomOptions={room.options}
                selectedOption={getRoomSelectedOption(room.id)}
                onChange={handleOptionChange}
                layout="stacked"
              />
              <div className='mb-[12px] flex items-center justify-center'>
                  <svg width="100%" height="1px">
                    <line 
                      x1="0" y1="1" x2="100%" y2="1" 
                      stroke="#f0f0f0" 
                      strokeWidth="2" 
                      strokeDasharray="10 4"
                    />
                  </svg>
              </div>
             
             
              
              {/* Pricing */}
              <div className="flex items-center justify-between">
                <div>
                  {renderRoomPriceLabel(room, { compact: true })}
                  {!room.comingSoon &&
                    (room.taxes && typeof room.taxes === 'number' ? (
                      <p className="text-[12px] text-[#999]">Includes ₹{room.taxes} taxes & fees</p>
                    ) : (
                      <p className="text-[12px] text-[#999]">Taxes & fees included</p>
                    ))}
                </div>
                
                {/* Room Selection Controls */}
                {renderRoomAddControls(room)}
              </div>
            </div>
          </div>
        );
        })}
      </div>

      {/* Selected Rooms Summary & Next Button */}
      {selectedRooms.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-50">
          <div className="max-w-md mx-auto">
            {/* Selected Rooms Summary */}
            {/* <div className="mb-4">
              <h3 className="text-[16px] font-medium text-gray-800 mb-3">Selected Rooms</h3>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {selectedRooms.map(room => (
                  <div key={room.id} className="flex items-center justify-between text-sm">
                    <div className="flex-1">
                      <span className="font-medium">{room.name}</span>
                      <span className="text-gray-500 ml-2">
                        ({roomQuantities[room.id]} × ₹{typeof room.price === 'number' ? room.price.toLocaleString() : 'N/A'})
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleRemoveRoom(room.id)}
                        className="w-6 h-6 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors flex items-center justify-center"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div> */}
            
            {/* Total and Next Button */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[14px] text-gray-600">
                  Total: <span className="font-medium text-gray-800">{getTotalRooms()} rooms</span>
                </p>
                <p className="text-[16px] font-bold text-black">
                  ₹{getTotalPrice().toLocaleString()}
                </p>
              </div>
              <button
                type="button"
                data-book-sniff="true"
                onClick={handleProceedToBooking}
                disabled={!hasSelectedDates || !selectedDates}
                  className={`h-[48px] rounded-[12px] text-[16px] px-[24px] w-[120px] font-semibold transition-colors ${hasSelectedDates && selectedDates
                    ? 'bg-black text-white hover:bg-gray-800'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
              Next
              </button>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Date Picker Modal */}
      {showDatePicker && (
        <DatePickerModal
          onClose={() => setShowDatePicker(false)}
          onOpenGuestSelector={() => {
            setShowDatePicker(false);
            setShowGuestSelector(true);
          }}
          onSelect={(dates) => {
            // Convert the DatePickerModal format to our handleDateSelect format
            if (dates.checkInDate && dates.checkOutDate) {
              handleDateSelect(dates.checkInDate, dates.checkOutDate);
            }
          }}
          initialDates={hasSelectedDates && selectedDates && selectedDates.checkInDate && selectedDates.checkOutDate ? {
            checkInDate: selectedDates.checkInDate,
            checkOutDate: selectedDates.checkOutDate
          } : null}
        />
      )}

      {/* Guest Selector Modal */}
      {showGuestSelector && (
        <GuestSelectorModal
          onClose={() => setShowGuestSelector(false)}
          onSelect={handleGuestSelect}
        />
      )}

      {/* Login Modal */}
      <LoginSignupModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        selectedRoom={selectedRoom}
        property={property}
        dates={selectedDates}
        onSuccess={() => {
          // After successful login, proceed to booking review
          console.log('Login successful, proceeding to booking review');
          
          // Prepare booking data based on selected room type
          let bookingData;
          if (selectedRoom?.type === 'single') {
            const pricing = buildStayPricingPayload(
              [{ ...selectedRoom, quantity: selectedRoom.quantity || 1 }],
              selectedDates
            );
            bookingData = {
              property,
              selectedRoom: selectedRoom,
              dates: selectedDates,
              user: user,
              pricing: {
                roomPrice: pricing.roomPrice,
                taxes: pricing.taxes,
                total: pricing.total
              }
            };
          } else {
            const pricing = buildStayPricingPayload(selectedRoom?.rooms || [], selectedDates);
            bookingData = {
              property,
              selectedRooms: selectedRoom?.rooms || [],
              dates: selectedDates,
              user: user,
              guestData: guestData,
              pricing: {
                roomPrice: pricing.roomPrice,
                taxes: pricing.taxes,
                total: pricing.total
              }
            };
          }
          
          navigate(`/property/${propertyId}/booking-review`, {
            state: { bookingData }
          });
        }}
      />
      
      {/* Debug info */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed top-4 right-4 bg-black text-white p-2 text-xs rounded max-w-xs">
          <div className="font-bold mb-1">Debug Info:</div>
          <div>Property ID: {property?.id || 'undefined'}</div>
          <div>Property Name: {property?.name || 'undefined'}</div>
          <div>Selected Rooms: {selectedRooms.length}</div>
          <div>Room Quantities: {JSON.stringify(roomQuantities)}</div>
          <div>Selected Options: {JSON.stringify(selectedOptions)}</div>
          <div className="mt-2 pt-2 border-t border-gray-600">
            <div>Authenticated: {isAuthenticated ? 'Yes' : 'No'}</div>
            <div>User: {user?.name || 'Not logged in'}</div>
            <div>Property ID: {propertyId}</div>
          </div>
          {selectedRoom && (
            <div className="mt-2 pt-2 border-t border-gray-600">
              <div>Selected Room Type: {selectedRoom.type}</div>
              {selectedRoom.type === 'multiple' && (
                <div>Total Rooms: {selectedRoom.rooms?.length || 0}</div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default RoomSelection;
